import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Truck, ChevronRight, AlertCircle, Package, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = [
  "Draft", "Submitted", "Waiting for approval", "Approved", "Rejected",
  "Picking", "Packed", "Sent", "Delivered", "Installed", "Verified", "Closed",
];
const TONE = {
  "Draft": "bg-slate-100 text-slate-700",
  "Submitted": "bg-blue-100 text-blue-800",
  "Waiting for approval": "bg-amber-100 text-amber-800",
  "Approved": "bg-emerald-100 text-emerald-800",
  "Rejected": "bg-red-100 text-red-800",
  "Picking": "bg-indigo-100 text-indigo-800",
  "Packed": "bg-indigo-100 text-indigo-800",
  "Sent": "bg-cyan-100 text-cyan-800",
  "Delivered": "bg-teal-100 text-teal-800",
  "Installed": "bg-lime-100 text-lime-800",
  "Verified": "bg-emerald-100 text-emerald-800",
  "Closed": "bg-slate-200 text-slate-700",
};

export default function POSRequests() {
  const { user } = useAuth();
  const canApprove = ["super_admin", "management", "general_manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(null);

  const load = () => {
    const params = statusFilter ? { status: statusFilter } : {};
    api.get("/pos-requests", { params }).then(r => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (rid, status) => {
    try { await api.patch(`/pos-requests/${rid}/status`, { status }); toast.success(`Status: ${status}`); load(); if (open) setOpen({ ...open, status }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="pos-requests-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight flex items-center gap-2"><Truck className="w-8 h-8 text-[#003DA5]" /> POS Requests</h1>
          <p className="text-slate-600 mt-1">Track POS material requests through the 12-status pipeline</p>
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-11 px-3 border rounded-lg text-sm" data-testid="pos-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {STATUSES.map(s => (
          <button key={s} onClick={() => setStatusFilter(s === statusFilter ? "" : s)} className={`p-2.5 rounded-lg border text-left ${statusFilter === s ? "border-[#003DA5] bg-[#F3F7FC]" : "border-slate-200 bg-white"}`} data-testid={`kpi-${s.replace(/\s/g, "-")}`}>
            <div className="text-[10px] uppercase font-semibold text-slate-500">{s}</div>
            <div className="text-xl font-heading font-bold text-[#001F54]">{items.filter(i => i.status === s).length}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Store</th>
              <th className="text-left px-4 py-3">Reason</th>
              <th className="text-left px-4 py-3">Items</th>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(r)} data-testid={`request-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.customer_name}<div className="text-xs text-slate-500">{r.customer_group}</div></td>
                <td className="px-4 py-3 text-xs">{r.reason}</td>
                <td className="px-4 py-3 text-xs">{r.items.length} item{r.items.length !== 1 ? "s" : ""}</td>
                <td className="px-4 py-3"><Priority p={r.priority} /></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${TONE[r.status] || "bg-slate-100"}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-500">No POS requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && <RequestDetail request={open} onClose={() => setOpen(null)} canApprove={canApprove} onStatus={(s) => updateStatus(open.id, s)} isAgent={user?.role === "sales_agent"} />}
    </div>
  );
}

const Priority = ({ p }) => {
  const c = p === "Urgent" ? "bg-red-100 text-red-800" : p === "High" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c}`}>{p || "Normal"}</span>;
};

function RequestDetail({ request: r, onClose, canApprove, onStatus, isAgent }) {
  const agentAllowed = ["Draft", "Submitted", "Waiting for approval", "Installed", "Verified"];
  const availableStatuses = isAgent ? agentAllowed : STATUSES;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex justify-between items-start">
          <div>
            <h3 className="text-xl font-heading font-semibold">POS Request — {r.customer_name}</h3>
            <div className="text-xs text-slate-500 mt-1">{r.reason} • Priority: {r.priority} • Created {new Date(r.created_at).toLocaleString()}</div>
          </div>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-xs uppercase text-slate-500">Customer group</span><div>{r.customer_group || "—"}</div></div>
            <div><span className="text-xs uppercase text-slate-500">State</span><div>{r.state || "—"}</div></div>
            <div><span className="text-xs uppercase text-slate-500">Delivery address</span><div>{r.delivery_address || "—"}</div></div>
            <div><span className="text-xs uppercase text-slate-500">Sales agent</span><div>{r.agent_name || "—"}</div></div>
          </div>
          <div>
            <div className="text-xs uppercase font-semibold text-slate-500 mb-2">Items ({r.items.length})</div>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {r.items.map((it, i) => (
                <li key={i} className="p-3 text-sm flex justify-between" data-testid={`req-item-${i}`}>
                  <div>
                    <div className="font-medium">{it.material_name}</div>
                    <div className="text-xs text-slate-500">{it.model_name}{it.size ? ` • ${it.size}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-[#003DA5]">{it.quantity_needed}</div>
                    <div className="text-[10px] text-slate-500">of {it.quantity_required} required</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {r.notes && <div><span className="text-xs uppercase text-slate-500">Notes</span><div className="text-sm mt-1 p-2 bg-[#F3F7FC] rounded">{r.notes}</div></div>}
          <div>
            <div className="text-xs uppercase font-semibold text-slate-500 mb-2">Status history</div>
            <ul className="text-xs space-y-1">
              {(r.status_history || []).map((h, i) => (
                <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                  <span><span className={`px-2 py-0.5 rounded-full font-semibold ${TONE[h.status] || "bg-slate-100"}`}>{h.status}</span> {h.notes && <span className="text-slate-500 italic ml-1">— {h.notes}</span>}</span>
                  <span className="text-slate-500">{h.by_name} • {new Date(h.at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="p-5 border-t space-y-2">
          <div className="text-xs uppercase font-semibold text-slate-500">Advance status</div>
          <div className="flex flex-wrap gap-2">
            {availableStatuses.map(s => (
              <button key={s} disabled={s === r.status} onClick={() => onStatus(s)}
                className={`h-9 px-3 rounded-lg text-xs font-semibold border ${s === r.status ? "bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed" : "border-[#003DA5] text-[#003DA5] hover:bg-[#F3F7FC]"}`}
                data-testid={`advance-${s.replace(/\s/g, "-")}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
